// src/components/Auth/Register.tsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from './AuthLayout';
import TurnstileWidget from './TurnstileWidget';

const schema = yup.object().shape({
    username: yup.string().required('Vui lòng nhập tên đăng nhập'),
    password: yup.string().min(6, 'Mật khẩu phải có ít nhất 6 ký tự').required('Vui lòng nhập mật khẩu'),
    confirmPassword: yup.string()
        .oneOf([yup.ref('password')], 'Mật khẩu xác nhận không khớp')
        .required('Vui lòng xác nhận mật khẩu'),
});

interface RegisterForm {
    username: string;
    password: string;
    confirmPassword: string;
}

const Register: React.FC = () => {
    const navigate = useNavigate();
    const { register: registerUser, isLoading, error } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [registerError, setRegisterError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [captchaToken, setCaptchaToken] = useState('');
    const [captchaInstance, setCaptchaInstance] = useState(0);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<RegisterForm>({
        resolver: yupResolver(schema),
    });

    const onSubmit = async (data: RegisterForm) => {
        setRegisterError(null);
        setSuccessMessage(null);
        if (!captchaToken) {
            setRegisterError('Vui lòng xác minh CAPTCHA.');
            return;
        }
        try {
            await registerUser({
                username: data.username,
                password: data.password,
                captchaToken,
            });
            setSuccessMessage('Đăng ký thành công! Đang chuyển vào trang chính...');
            navigate('/', { replace: true });
        } catch (err: any) {
            setRegisterError(err.message || 'Đăng ký thất bại');
        } finally {
            setCaptchaToken('');
            setCaptchaInstance((instance) => instance + 1);
        }
    };

    return (
        <AuthLayout title="Tạo tài khoản" subtitle="Đăng ký để bắt đầu">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tên đăng nhập *
                    </label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">👤</span>
                        <input
                            {...register('username')}
                            type="text"
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Chọn tên đăng nhập"
                        />
                    </div>
                    {errors.username && (
                        <p className="text-sm text-red-500 mt-1">{errors.username.message}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mật khẩu *
                    </label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">🔒</span>
                        <input
                            {...register('password')}
                            type={showPassword ? 'text' : 'password'}
                            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Tạo mật khẩu"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            {showPassword ? '🙈' : '👁️'}
                        </button>
                    </div>
                    {errors.password && (
                        <p className="text-sm text-red-500 mt-1">{errors.password.message}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Xác nhận mật khẩu *
                    </label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">🔒</span>
                        <input
                            {...register('confirmPassword')}
                            type={showConfirmPassword ? 'text' : 'password'}
                            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Nhập lại mật khẩu"
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            {showConfirmPassword ? '🙈' : '👁️'}
                        </button>
                    </div>
                    {errors.confirmPassword && (
                        <p className="text-sm text-red-500 mt-1">{errors.confirmPassword.message}</p>
                    )}
                </div>

                <TurnstileWidget key={captchaInstance} onVerify={setCaptchaToken} />

                {(error || registerError) && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm">
                        {error || registerError}
                    </div>
                )}

                {successMessage && (
                    <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-2 rounded-lg text-sm">
                        {successMessage}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={isLoading || !captchaToken}
                    className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Đang tạo tài khoản...
                        </span>
                    ) : (
                        'Tạo tài khoản'
                    )}
                </button>

                <p className="text-center text-sm text-gray-600">
                    Đã có tài khoản?{' '}
                    <Link to="/login" className="text-blue-500 hover:text-blue-600 font-medium">
                        Đăng nhập
                    </Link>
                </p>
            </form>
        </AuthLayout>
    );
};

export default Register;